import React from "react";
import { lazy, Suspense } from 'react';
import { Routes, Route } from "react-router-dom";
import { Loader } from "../components/ui/loader/loader";
import ProtectedRoute from "../components/auth/ProtectedRoute";
import { APP_ROUTE_MAP } from "../config/accessControl";
import { Layout } from "../pages/layout/layout";
import SyncBrowserRouter from "./SyncBrowserRouter";
import { UserAppStateTracker } from "../hooks/useUserAppState";


const Dashboard = lazy(() => import("../pages/dashboard/dashboard"));
const Reporting = lazy(() => import("../pages/dashboard/reporting"));
const PublicForm = lazy(() => import('../pages/forms/publicForm'))
const Login = lazy(() => import('../pages/login/login'))
const AddProject = lazy(() => import("../pages/forms/addProjects"));
const AddProgram = lazy(() => import("../pages/forms/addPrograms"));
const AddUser = lazy(() => import("../pages/forms/addUsers"));
const AddRole = lazy(() => import("../pages/forms/addRole"));
const AddProgramData = lazy(() => import("../pages/forms/addProgramsData"));
const AddProgramFields = lazy(() => import("../pages/forms/addProgramField"));
const AddProgramAnalytics = lazy(() => import("../pages/forms/addProgramAnalytics"));
const DynamicAnalytics = lazy(() => import("../pages/forms/dynamicAnalytics"));
const DynamicCharts = lazy(() => import("../pages/forms/dynamicCharts"));
const ViewEntryData = lazy(() => import("../pages/forms/viewEntryData"));
const AddSubProgram = lazy(() => import("../pages/forms/addSubProgram"));
const AddSubProgramFields = lazy(() => import("../pages/forms/addSubProgramField"));
const AddRecords = lazy(() => import("../pages/forms/addRecords"));
const AddBenRecords = lazy(() => import("../pages/forms/addBenRecords"));
const EventData = lazy(() => import("../pages/forms/eventData"));
const CenterProfile = lazy(() => import("../pages/forms/centerProfile"));
const AddPartner = lazy(() => import("../pages/forms/addPartner"));
const PartnerForm = lazy(() => import("../pages/forms/partnerForm"));
const Attendance = lazy(() => import("../pages/forms/attendance"));
const SubCenterProfile = lazy(() => import("../pages/forms/subCenterProfile"));


const AppRoutes = () => {
  const routeList = [
    {
      path: "/",
      element: <Login />,
      exact: true,
      layout: false,
    },
    {
      path: "/login",
      element: <Login />,
      exact: true,
      layout: false,
    },
    {
      path: "/dashboard",
      element: <Dashboard />,
      exact: true,
      layout: true,
    },
    {
      path: "/reporting",
      element: <Reporting />,
      exact: true,
      layout: true,
    },
    {
      path: "/add-data",
      element: <AddProgramData />,
      exact: true,
      layout: true,
    },
    {
      path: "/add-fields",
      element: <AddProgramFields />,
      exact: true,
      layout: true,
    },
    {
      path: "/addproject",
      element: <AddProject />,
      exact: true,
      layout: true,
    },
    {
      path: "/addprogram",
      element: <AddProgram />,
      exact: true,
      layout: true,
    },
    {
      path: "/adduser",
      element: <AddUser />,
      exact: true,
      layout: true,
    },
     {
      path: "/addrole",
      element: <AddRole />,
      exact: true,
      layout: true,
    },
    {
      path: "/add-analytics",
      element: <AddProgramAnalytics/>,
      exact: true,
      layout: true,
    },
    {
      path: "/dynamic-analytics",
      element: <DynamicAnalytics />,
      exact: true,
      layout: true,
    },
    {
      path: "/dynamic-charts",
      element: <DynamicCharts />,
      exact: true,
      layout: true,
    },
    {
      path: "/view-entry-data",
      element: <ViewEntryData/>,
      exact: true,
      layout: true,
    },
    {
      path: "/events",
      element: <EventData />,
      exact: true,
      layout: true,
    },
    {
      path: "/add-sub-program",
      element: <AddSubProgram/>,
      exact: true,
      layout: true,
    },
    {
      path: "/add-subprogram-fields",
      element: <AddSubProgramFields/>,
      exact: true,
      layout: true,
    },
    {
      path: "/view-entry-data/add-record",
      element: <AddRecords/>,
      exact: true,
      layout: true,
    },
    {
      path: "/view-entry-data/add-ben-record",
      element: <AddBenRecords />,
      exact: true,
      layout: true,
    },
    { 
      path: "/public-form",
      element: <PublicForm/>,
      exact: true,
      layout: true,
    },
    {
      path: "/group-profile",
      element: <CenterProfile />,
      exact: true,
      layout: true,
    },
    {
      path: "/subgroup-profile",
      element: <SubCenterProfile/>,
      exact: true,
      layout: true,
    },
    {
      path: "/add-partner",
      element: <AddPartner />, 
      exact: true,
      layout: true,
    },
    {
      path: "/partner-form",
      element: <PartnerForm />,
      exact: true,
      layout: true,
    }, 
    {
      path: "/attendance",
      element: <Attendance/>,
      exact: true,
      layout: true,
    },
  ];

  return (
    // SyncBrowserRouter (not BrowserRouter): RR v7 wraps location updates in
    // startTransition, which updates the URL while keeping the old page on
    // screen. That broke sidebar navigations and in-page tabs (e.g. View
    // Analytics) until a hard refresh. Sync commits keep UI and URL aligned.
    <SyncBrowserRouter>
      {/* Phase 3: persist last meaningful route for the authenticated user. */}
      <UserAppStateTracker />
      <Routes>
        {routeList?.map(({ path, element, layout }) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute routeRule={APP_ROUTE_MAP[path]}>
                {layout ? (
                  <Layout>
                    {/* Suspense sits inside Layout so a lazy page chunk never
                        unmounts the sidebar/header — the shell paints straight
                        away and only the content area shows the loader. */}
                    <Suspense fallback={<Loader />}>
                      {element}
                    </Suspense>
                  </Layout>
                ) : (
                  <Suspense fallback={<Loader />}>
                    {element}
                  </Suspense>
                )}
              </ProtectedRoute>
            }
          />
        ))}
      </Routes>
    </SyncBrowserRouter>
  );
};

export default AppRoutes;
